import { CemFeatureModel, EntityLevelEnum } from '@rsmus/ecp-userservice'
import { z } from 'zod'
import { FEATURE_MANAGEMENT } from '../../envVariables'
import { FeatureFlag } from '../../utils/constants/constants'

export const isCemFeatureEnabled = (
  feature: string | string[],
  enabledFeatures: CemFeatureModel[],
  matchType: 'all' | 'any',
  entityLevel: EntityLevelEnum = EntityLevelEnum.Any,
  entityId: string | null = null,
): boolean => {
  if (enabledFeatures === undefined) {
    return false
  }
  const features = !Array.isArray(feature) ? [feature] : feature
  const checkFeature = (f: string) =>
    enabledFeatures.some(
      (ef) =>
        ef.featureName === f &&
        ef.entities?.some(
          (e) =>
            (entityLevel === EntityLevelEnum.Any ||
              e.entityLevel === entityLevel) &&
            (entityId === null || e.entityId === entityId),
        ),
    )
  return (
    features?.length > 0 &&
    (matchType === 'all'
      ? features.every(checkFeature)
      : features.some(checkFeature))
  )
}

const AzureAppConfigFeatureFilterSchema = z.object({
  EnabledFor: z.array(
    z.object({
      Name: z.string(),
      Parameters: z.object({
        Audience: z.object({
          DefaultRolloutPercentage: z.number(),
          Users: z.array(z.string()),
          Groups: z.array(
            z.object({
              Name: z.string(),
              RolloutPercentage: z.number(),
            }),
          ),
          Exclusion: z.optional(
            z.object({
              Users: z.array(z.string()),
              Groups: z.array(z.string()),
            }),
          ),
        }),
      }),
    }),
  ),
})

const isSingleFeatureFlagEnabled = (
  feature: FeatureFlag,
  userId: string | undefined,
  userCemFeatures: string[],
  featureState?: any,
): boolean => {
  if (FEATURE_MANAGEMENT === undefined) {
    return false
  }
  const featuresFromSource =
    typeof FEATURE_MANAGEMENT === 'string'
      ? JSON.parse(FEATURE_MANAGEMENT)
      : FEATURE_MANAGEMENT
  const features = featureState != null ? featureState : featuresFromSource
  if (features[feature] === undefined) {
    throw new Error(`Feature flag '${feature}' not found`)
  }
  if (typeof features[feature] === 'boolean') {
    return features[feature]
  }
  if (!userId?.trim()) {
    return false
  }
  const parseResult = AzureAppConfigFeatureFilterSchema.safeParse(
    features[feature],
  )
  if (
    !parseResult.success ||
    parseResult.data.EnabledFor.some(
      (o) =>
        o.Name !== 'Microsoft.Targeting' ||
        (o.Parameters.Audience.DefaultRolloutPercentage !== 0 &&
          o.Parameters.Audience.DefaultRolloutPercentage !== 100) ||
        o.Parameters.Audience.Groups.some(
          (g) => g.RolloutPercentage !== 0 && g.RolloutPercentage !== 100,
        ),
    )
  ) {
    throw new Error(
      "Not implemented: Only Feature flags of type 'Microsoft.Targeting' with DefaultRolloutPercentage=0|100 and Groups[].RolloutPercentage=0|100 are supported.",
    )
  }
  let isFeatureOnForUser = false
  parseResult.data.EnabledFor.forEach((filter) => {
    if (filter.Name === 'Microsoft.Targeting') {
      // Check if feature filter is default on for all users, feature ON
      isFeatureOnForUser =
        filter.Parameters.Audience.DefaultRolloutPercentage === 100
      // If feature filter groups align with current user's CEM features, feature ON
      filter.Parameters.Audience.Groups.forEach((group) => {
        if (
          userCemFeatures.some(
            (f) =>
              group.RolloutPercentage === 100 &&
              f.toLowerCase() === group.Name.toLowerCase(),
          )
        ) {
          isFeatureOnForUser = true
        }
      })
      // If feature filter users contains current user, feature ON
      filter.Parameters.Audience.Users.forEach((user) => {
        if (user.toLowerCase() === userId.toLowerCase())
          isFeatureOnForUser = true
      })
      if (filter.Parameters.Audience.Exclusion != null) {
        // If feature filter has exclusion groups that align with current user's CEM features, feature OFF
        filter.Parameters.Audience.Exclusion.Groups.forEach((excludeGroup) => {
          if (
            userCemFeatures.some(
              (f) => f.toLowerCase() === excludeGroup.toLowerCase(),
            )
          ) {
            isFeatureOnForUser = false
          }
        })
        // If feature filter has exclusion users contains current user, feature OFF
        filter.Parameters.Audience.Exclusion.Users.forEach((excludeUser) => {
          if (excludeUser.toLowerCase() === userId.toLowerCase()) {
            isFeatureOnForUser = false
          }
        })
      }
    }
  })
  return isFeatureOnForUser
}

export const isFeatureFlagEnabled = (
  feature: FeatureFlag | FeatureFlag[],
  userId: string | undefined,
  cemFeatures: CemFeatureModel[],
  matchType: 'all' | 'any' = 'all',
): boolean => {
  if (FEATURE_MANAGEMENT === undefined) {
    return false
  }
  const featureData =
    typeof FEATURE_MANAGEMENT === 'string'
      ? JSON.parse(FEATURE_MANAGEMENT)
      : FEATURE_MANAGEMENT
  const featuresToCheck = !Array.isArray(feature) ? [feature] : feature
  return (
    featuresToCheck?.length > 0 &&
    (matchType === 'all'
      ? featuresToCheck.every((f) =>
          isSingleFeatureFlagEnabled(
            f,
            userId,
            cemFeatures
              .filter((cemF) => cemF !== undefined)
              .map((cemF) => cemF.featureName ?? ''),
            featureData,
          ),
        )
      : featuresToCheck.some((f) =>
          isSingleFeatureFlagEnabled(
            f,
            userId,
            cemFeatures
              .filter((cemF) => cemF !== undefined)
              .map((cemF) => cemF.featureName ?? ''),
            featureData,
          ),
        ))
  )
}
